import { useState, useEffect } from 'react';
import { Users, Video, CreditCard, TrendingUp, ArrowUp, ArrowDown, MessageSquare } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { api } from '../utils/api';
import Pagination from '../components/Pagination';

const tooltipStyle = {
  contentStyle: { background: 'var(--color-bg)', border: '1px solid var(--glass-border)', borderRadius: 10, fontSize: 11 },
  labelStyle: { color: 'var(--color-muted)' },
};

interface DashboardStats {
  totalUsers: number;
  totalVideos: number;
  totalInvestmentQueries: number;
  totalRevenue: number;
  userChange: { value: string; up: boolean };
  videoChange: { value: string; up: boolean };
  queryChange: { value: string; up: boolean };
  revenueChange: { value: string; up: boolean };
  revenueData: { month: string; value: number }[];
  userGrowth: { month: string; value: number }[];
}

export default function AdminDashboard() {
  const [users, setUsers] = useState<any[]>([]);
  const [videos, setVideos] = useState<any[]>([]);
  const [queries, setQueries] = useState<any[]>([]);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const [usersRes, videosRes, queriesRes, dashboardRes] = await Promise.all([
        api.get(`/admin/users?page=${page}&limit=10`),
        api.get(`/videos?page=${page}&limit=10`),
        api.get(`/queries/admin?page=${page}&limit=10`),
        api.get('/admin/dashboard/stats')
      ]);

      setUsers(usersRes.data || []);
      setVideos(videosRes.data || []);
      setQueries(queriesRes.data || []);
      setDashboardStats(dashboardRes.data);
      setPagination(usersRes.pagination || { page: 1, totalPages: 1, total: 0 });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1);
  }, []);

  const stats = dashboardStats ? [
    {
      label: 'Total Users',
      value: dashboardStats.totalUsers,
      icon: Users,
      change: dashboardStats.userChange.value,
      up: dashboardStats.userChange.up,
      color: 'var(--color-accent)'
    },
    {
      label: 'Total Videos',
      value: dashboardStats.totalVideos,
      icon: Video,
      change: dashboardStats.videoChange.value,
      up: dashboardStats.videoChange.up,
      color: 'var(--color-blue)'
    },
    {
      label: 'Investment Queries',
      value: dashboardStats.totalInvestmentQueries,
      icon: MessageSquare,
      change: dashboardStats.queryChange.value,
      up: dashboardStats.queryChange.up,
      color: '#d97706'
    },
    {
      label: 'Total Revenue',
      value: `₹${dashboardStats.totalRevenue.toLocaleString()}`,
      icon: TrendingUp,
      change: dashboardStats.revenueChange.value,
      up: dashboardStats.revenueChange.up,
      color: '#7c3aed'
    }
  ] : [];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="card p-4 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-1/2 mb-3"></div>
              <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-black text-[var(--color-text)] tracking-tight">Dashboard</h1>
        <p className="text-sm text-[var(--color-muted)]">Platform analytics and quick overview</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {stats.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="stat-card">
              <div className="flex items-center justify-between mb-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${s.color}18` }}>
                  <Icon className="w-4 h-4" style={{ color: s.color }} />
                </div>
                <span className={`text-xs font-bold flex items-center gap-0.5 ${s.up ? 'text-[var(--color-accent)]' : 'text-red-500'}`}>
                  {s.up ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                  {s.change}
                </span>
              </div>
              <p className="stat-card-value">{s.value}</p>
              <p className="stat-card-label">{s.label}</p>
            </div>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
        <div className="card p-5">
          <h3 className="text-sm font-bold text-[var(--color-text)] mb-1">Revenue (₹)</h3>
          <p className="text-xs text-[var(--color-muted)] mb-4">Monthly revenue trend</p>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dashboardStats?.revenueData || []}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#5f9c4c" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#5f9c4c" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--glass-border)" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: 'var(--color-muted)' }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip {...tooltipStyle} />
                <Area type="monotone" dataKey="value" stroke="#5f9c4c" strokeWidth={2} fill="url(#rev)" name="Revenue" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card p-5">
          <h3 className="text-sm font-bold text-[var(--color-text)] mb-1">User Growth</h3>
          <p className="text-xs text-[var(--color-muted)] mb-4">Cumulative registered users</p>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dashboardStats?.userGrowth || []} barSize={14}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--glass-border)" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: 'var(--color-muted)' }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip {...tooltipStyle} />
                <Bar dataKey="value" fill="var(--color-blue)" radius={[4, 4, 0, 0]} name="Users" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent users */}
      <div className="card">
        <div className="p-4 sm:p-5 border-b border-[var(--glass-border)]">
          <h3 className="text-sm font-bold text-[var(--color-text)]">Recent Registrations</h3>
        </div>
        <div className="p-4 sm:p-5">
          {/* Mobile card layout */}
          <div className="sm:hidden space-y-3">
            {users.slice(0, 6).map(u => (
              <div key={u.id} className="bg-[var(--glass-bg)] rounded-xl p-3 border border-[var(--glass-border)]">
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-[var(--color-text)] truncate">{u.email}</p>
                    <p className="text-[10px] text-[var(--color-muted)]">{new Date(u.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className={`badge ${u.role === 'admin' ? 'badge-purple' : 'badge-blue'} ml-2`}>{u.role}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="badge badge-green">● Active</span>
                </div>
              </div>
            ))}
            {users.length === 0 && (
              <div className="text-center text-[var(--color-muted)] py-8 text-sm">No users found</div>
            )}
          </div>

          {/* Desktop table layout */}
          <div className="hidden sm:block overflow-x-auto">

            <table className="tbl">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Joined</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {users.slice(0, 6).map(u => (
                  <tr key={u.id}>
                    <td className="font-medium">{u.email}</td>
                    <td><span className={`badge ${u.role === 'admin' ? 'badge-purple' : 'badge-blue'}`}>{u.role}</span></td>
                    <td className="text-[var(--color-muted)]">{new Date(u.created_at).toLocaleDateString()}</td>
                    <td><span className="badge badge-green">● Active</span></td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={4} className="text-center text-[var(--color-muted)] py-8">No users found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Pagination
        page={pagination.page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        onPageChange={fetchData}
      />
    </div>
  );
}
